
// The cart countdown was removed (Oct 2026): drop the deadline earlier visits stored.
try {
  window.localStorage.removeItem('woodeca:cart-countdown');
} catch {
  // storage unavailable: nothing to clean up
}
/**
 * Slide-in cart.
 *
 * Quantity changes post to /cart/change.js and then pull the drawer section back
 * through the Section Rendering API, so every price, the subtotal and the item
 * count are re-rendered by Liquid. That is why nothing here formats money: the
 * only thing JavaScript owns is which markup is on screen.
 *
 * One delegated document listener drives everything, so replacing the drawer's
 * innards after a re-render never leaves a dead handler behind.
 */

const SECTION_ID = 'showcase-cart-drawer';
const OPEN_CLASS = 'cart-drawer--open';
const BODY_LOCK = 'cart-drawer-locked';
const LOADING_CLASS = 'cart-drawer--loading';

/** Guards against a second change landing while the first is still in flight. */
let busy = false;

const root = () => window.Shopify?.routes?.root ?? '/';

const drawer = () => document.querySelector('[data-cart-drawer]');

/* ---------------------------------------------------------------- pending adds */

/*
  An add opens the drawer on the click, before Shopify has answered: the drawer
  shows "Adding to cart…" over the current cart, and the confirmed cart replaces
  it when the add returns. Counted, so two quick adds keep the loading state
  until both have landed.
*/
let pendingAdds = 0;

/**
 * @param {Element | null | undefined} el - The drawer root.
 * @param {boolean} on
 */
function setLoading(el, on) {
  if (!(el instanceof HTMLElement)) return;

  el.classList.toggle(LOADING_CLASS, on);
  const status = el.querySelector('[data-cart-loading]');
  if (status instanceof HTMLElement) status.hidden = !on;

  const panel = el.querySelector('.cart-drawer__panel');
  if (on) panel?.setAttribute('aria-busy', 'true');
  else panel?.removeAttribute('aria-busy');
}

function beginAdd() {
  pendingAdds += 1;
  const el = drawer();
  clearAddError();
  setLoading(el, true);
  if (el instanceof HTMLElement && !el.classList.contains(OPEN_CLASS)) openDrawer();
}

function endAdd() {
  pendingAdds = Math.max(0, pendingAdds - 1);
  if (pendingAdds === 0) setLoading(drawer(), false);
}

/**
 * Says, inside the open drawer, why an add didn't happen.
 *
 * @param {string} [message] - Shopify's own reason, when it gave one.
 */
function showAddError(message) {
  const el = drawer();
  const box = el?.querySelector('[data-cart-error]');
  if (!(el instanceof HTMLElement) || !(box instanceof HTMLElement)) return;

  box.textContent = message || el.dataset.addErrorText || 'This item could not be added. Please try again.';
  box.hidden = false;
}

function clearAddError() {
  const box = drawer()?.querySelector('[data-cart-error]');
  if (!(box instanceof HTMLElement)) return;
  box.hidden = true;
  box.textContent = '';
}

/* ------------------------------------------------------------------ open/close */

function openDrawer() {
  const el = drawer();
  if (!(el instanceof HTMLElement)) return;

  el.hidden = false;
  requestAnimationFrame(() => el.classList.add(OPEN_CLASS));
  document.body.classList.add(BODY_LOCK);

  const close = el.querySelector('.cart-drawer__close');
  if (close instanceof HTMLElement) close.focus();
}

function closeDrawer() {
  const el = drawer();
  if (!(el instanceof HTMLElement)) return;

  el.classList.remove(OPEN_CLASS);
  document.body.classList.remove(BODY_LOCK);

  const done = () => {
    el.hidden = true;
  };
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
  else setTimeout(done, 240);
}

/* --------------------------------------------------------------- cart mutation */

/**
 * Applies a quantity change and swaps in the freshly rendered drawer.
 *
 * @param {number} line 1-based line index, as Shopify numbers them
 * @param {number} quantity
 */
async function changeLine(line, quantity) {
  if (busy) return;
  busy = true;

  const panel = drawer()?.querySelector('.cart-drawer__panel');
  panel?.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(`${root()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity, sections: SECTION_ID }),
    });

    if (response.status === 422) {
      // Past the stock limit. Shopify keeps the line at the most it can hold;
      // re-render so the quantity and total are the real ones, then say why.
      await refreshDrawer();
      showLimitNotice(drawer()?.querySelector(`[data-cart-line="${line}"]`));
      return;
    }
    if (!response.ok) throw new Error(`cart/change.js responded ${response.status}`);

    const payload = await response.json();
    replaceDrawer(payload?.sections?.[SECTION_ID]);
    syncCartCount(payload?.item_count ?? 0);
  } catch (error) {
    console.error('[showcase-cart-drawer]', error);
  } finally {
    busy = false;
    panel?.removeAttribute('aria-busy');
  }
}

/**
 * Swaps the drawer's markup for a freshly rendered copy, preserving the open
 * state — the section renders with `hidden` set, which would otherwise slam it
 * shut the moment a quantity changed.
 *
 * @param {string | undefined} html
 */
function replaceDrawer(html) {
  const current = drawer();
  if (!html || !(current instanceof HTMLElement)) return;

  const wasOpen = current.classList.contains(OPEN_CLASS);

  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const next = parsed.querySelector('[data-cart-drawer]');
  if (!(next instanceof HTMLElement)) return;

  if (wasOpen) {
    next.hidden = false;
    next.classList.add(OPEN_CLASS);
  }

  current.replaceWith(next);
  // Another add still on its way: the fresh markup keeps saying so.
  if (pendingAdds > 0) setLoading(next, true);

  // The freshly rendered section carries the authoritative item count, so every
  // path that refreshes the drawer — any add, any quantity change — also keeps
  // the header badge right without a second request.
  const count = Number(next.dataset.cartCount);
  if (Number.isFinite(count)) syncCartCount(count);
}

/**
 * Tells the shopper why a line stopped at its quantity: "Only 2 available".
 *
 * @param {Element | null | undefined} lineElement
 */
function showLimitNotice(lineElement) {
  if (!(lineElement instanceof HTMLElement)) return;

  const notice = lineElement.querySelector('[data-cart-line-notice]');
  const quantity = lineElement.querySelector('.cart-quantity__value')?.textContent?.trim();
  const template = drawer()?.dataset.limitText || 'Only [count] available';
  if (!(notice instanceof HTMLElement) || !quantity) return;

  notice.textContent = template.replace('[count]', quantity);
  notice.hidden = false;
}

/** @param {string | number} variantId */
function showLimitForVariant(variantId) {
  showLimitNotice(drawer()?.querySelector(`[data-variant-id="${variantId}"]`));
}

/** @param {number} count */
function syncCartCount(count) {
  for (const node of document.querySelectorAll('.showcase-header__cart-count')) {
    node.textContent = String(count);
    node.hidden = count === 0;
  }
}

/* ------------------------------------------------------------------ listeners */

document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement | null} */ (event.target);
  if (!target?.closest) return;

  if (target.closest('[data-cart-close]')) {
    event.preventDefault();
    closeDrawer();
    return;
  }

  const opener = target.closest('[data-cart-open]');
  if (opener) {
    // Modified clicks are the shopper asking for a new tab; leave them alone.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    refreshDrawer().then(openDrawer);
    return;
  }

  const step = target.closest('[data-cart-step]');
  if (step instanceof HTMLElement) {
    event.preventDefault();
    const line = Number(step.dataset.line);
    const value = step.parentElement?.querySelector('.cart-quantity__value')?.textContent ?? '1';
    const next = Number(value.trim()) + Number(step.dataset.cartStep);
    // Stepping below one removes the line, which is what the minus button at a
    // quantity of one is understood to mean.
    changeLine(line, Math.max(0, next));
    return;
  }

  const remove = target.closest('[data-cart-remove]');
  if (remove instanceof HTMLElement) {
    event.preventDefault();
    changeLine(Number(remove.dataset.line), 0);
  }
});

/**
 * Product-page add to cart. Intercepted here so it lands in the drawer instead
 * of navigating to /cart, while the form stays a real form for anyone without
 * JavaScript.
 *
 * Shopify's pixels count an add twice when it is both a submit of a form whose
 * action is /cart/add AND a /cart/add.js request: one tap became two
 * product_added_to_cart events, so two GA4 / Google Ads add_to_cart hits and
 * two Meta AddToCart. With this script running, the form's action is parked in
 * `data-cart-action`: the submit no longer looks like a cart add and only the
 * request below is counted. Without JavaScript nothing changes and the form
 * posts to /cart/add as before.
 */
const PRODUCT_FORM = '.product-buybox__form';

/** @param {ParentNode} root */
function parkProductFormActions(root) {
  for (const form of root.querySelectorAll(`${PRODUCT_FORM}[action]`)) {
    if (!(form instanceof HTMLFormElement)) continue;
    form.dataset.cartAction = form.getAttribute('action') ?? '';
    form.removeAttribute('action');
  }
}

parkProductFormActions(document);
// Theme editor: a re-rendered product section brings a fresh form.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) parkProductFormActions(event.target);
});

document.addEventListener('submit', async (event) => {
  const form = /** @type {HTMLFormElement} */ (event.target);
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('product-buybox__form')) return;

  // Buy It Now submits through its own handler and must still leave the page.
  const submitter = /** @type {HTMLElement | null} */ (event.submitter);
  if (submitter?.getAttribute('name') === 'checkout') {
    if (form.dataset.cartAction) form.setAttribute('action', form.dataset.cartAction);
    return;
  }

  event.preventDefault();

  // Sent as a JSON `items` add rather than the raw form data: the form-data
  // endpoint accepted more units than are in stock (6 of a 1-in-stock item),
  // leaving the shopper to find out at checkout. The items endpoint enforces
  // stock and reports it.
  const data = new FormData(form);
  const variantId = Number(data.get('id'));
  /** @type {Record<string, unknown>} */
  const item = { id: variantId, quantity: Number(data.get('quantity')) || 1 };
  const sellingPlan = data.get('selling_plan');
  if (sellingPlan) item.selling_plan = Number(sellingPlan);
  /** @type {Record<string, FormDataEntryValue>} */
  const properties = {};
  for (const [key, value] of data.entries()) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (match) properties[match[1]] = value;
  }
  if (Object.keys(properties).length) item.properties = properties;

  // The drawer opens now; the confirmed cart fills it when Shopify answers.
  beginAdd();

  /** Shopper-facing reason the add failed ('' = the default message); null = it worked. */
  let failure = null;
  let limited = false;

  try {
    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      // The drawer section comes back with the add, so no second round trip.
      body: JSON.stringify({ items: [item], sections: SECTION_ID }),
    });
    const payload = await response.json().catch(() => null);

    if (response.status === 422) {
      // Stock limit or sold out: Shopify added what it could, or nothing.
      // Show the cart as it really is, then say why.
      await refreshDrawer();
      limited = true;
      failure = payload?.description || payload?.message || '';
    } else if (!response.ok) {
      console.error('[showcase-cart-drawer] add', response.status, payload);
      failure = '';
    } else {
      const html = payload?.sections?.[SECTION_ID];
      if (html) replaceDrawer(html);
      else await refreshDrawer();
    }
  } catch (error) {
    console.error('[showcase-cart-drawer] add', error);
    failure = '';
  } finally {
    endAdd();
  }

  // A line for this variant means the cart holds the most there is: say so
  // under it. No line (sold out, network failure) gets the drawer-level message.
  if (limited && drawer()?.querySelector(`[data-variant-id="${variantId}"]`)) showLimitForVariant(variantId);
  else if (failure !== null) showAddError(failure);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  if (document.querySelector(`[data-cart-drawer].${OPEN_CLASS}`)) {
    event.preventDefault();
    closeDrawer();
  }
});

/*
  Card quick-add. `pending` opens the drawer in its loading state on the click;
  one of `open` (added), `limit` (stock limit) or `error` follows. A drawer
  the shopper closed while waiting stays closed.
*/
document.addEventListener('showcase:cart:pending', () => beginAdd());

// A card quick-add that hit the stock limit: show the line and the limit.
document.addEventListener('showcase:cart:limit', async (event) => {
  const wasPending = pendingAdds > 0;
  await refreshDrawer();
  endAdd();
  if (!wasPending) openDrawer();
  const detail = /** @type {CustomEvent<{ variantId?: string | number }>} */ (event).detail;
  if (detail?.variantId) showLimitForVariant(detail.variantId);
});

// The add succeeded. Callers that already have the rendered drawer (they asked
// for it with their add request) pass it as `detail.html`, saving a round trip.
document.addEventListener('showcase:cart:open', async (event) => {
  const wasPending = pendingAdds > 0;
  const html = /** @type {CustomEvent<{ html?: string }>} */ (event).detail?.html;
  if (html) replaceDrawer(html);
  else await refreshDrawer();
  endAdd();
  if (!wasPending) openDrawer();
});

// The add failed outright (sold out, network): say so in the open drawer.
document.addEventListener('showcase:cart:error', (event) => {
  endAdd();
  showAddError(/** @type {CustomEvent<{ message?: string }>} */ (event).detail?.message);
});

/*
  Stock Horizon product cards — search results, the 404 page, cart-page
  recommendations and their quick-add modal — add through Horizon's product
  form, which announces the add with the standard cart-lines-update event.
  Open this drawer for those adds too, so every add-to-cart on the site lands
  in the same place. Imported dynamically so a failure to load the events
  module can only cost this bridge, never the drawer itself.
*/
import('@shopify/events')
  .then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, onHorizonCartUpdate);
  })
  .catch((error) => console.warn('[showcase-cart-drawer] cart events unavailable', error));

/** @param {Event & { action?: string, promise?: Promise<{ detail?: { didError?: boolean } }> }} event */
function onHorizonCartUpdate(event) {
  // Quantity changes and removals (e.g. on the cart page) only need the drawer
  // markup and header badge brought up to date, not the drawer opened.
  if (event.action !== 'add' || window.location.pathname.replace(/\/$/, '').endsWith('/cart')) {
    event.promise?.then(() => refreshDrawer()).catch(() => {});
    return;
  }

  // Let a quick-add modal finish closing (and restore focus) before the
  // drawer takes over.
  const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;

  event.promise
    ?.then(({ detail } = {}) => {
      if (detail?.didError) return;
      const show = () => refreshDrawer().then(openDrawer);
      if (sourceModal instanceof HTMLDialogElement && sourceModal.open) {
        sourceModal.addEventListener('close', show, { once: true });
      } else {
        show();
      }
    })
    .catch(() => {
      // Horizon reports its own add errors inside the form.
    });
}

/** Pulls the drawer section fresh, so it reflects the add that just happened. */
async function refreshDrawer() {
  try {
    const response = await fetch(`${root()}?sections=${SECTION_ID}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`section render responded ${response.status}`);

    const payload = await response.json();
    replaceDrawer(payload?.[SECTION_ID]);
  } catch (error) {
    console.error('[showcase-cart-drawer] refresh', error);
  }
}
